import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { useAuth } from "../hooks/useAuth.js";
import { useTheme } from "../hooks/useTheme.js";
import { useTranslation, LOCALE_LABELS, type Locale } from "../i18n/index.js";
import { auth, usage, type UsageData } from "../lib/api.js";
import type { User } from "@ooo-codes/shared";

type Mode = "login" | "setup" | "invite" | "signup";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, needsSetup } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, locale, setLocale } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>(needsSetup ? "setup" : "login");

  const [usageData, setUsageData] = useState<UsageData | null>(null);

  // Invite code input
  const [inviteCode, setInviteCode] = useState("");

  useEffect(() => {
    if (needsSetup) setMode("setup");
  }, [needsSetup]);

  useEffect(() => {
    usage.get().then((res) => {
      if (res.success && res.data) setUsageData(res.data);
    }).catch(() => {});
  }, []);

  // Setup form state
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");

  async function handleLogin() {
    setError(null);
    setLoading(true);

    try {
      const optionsRes = await auth.loginOptions();
      if (!optionsRes.success || !optionsRes.data) {
        throw new Error("Failed to get login options");
      }

      const { options } = optionsRes.data as { options: unknown };

      const credential = await startAuthentication({
        optionsJSON:
          options as Parameters<typeof startAuthentication>[0]["optionsJSON"],
      });

      const verifyRes = await auth.loginVerify({
        credential,
        challenge: (options as { challenge: string }).challenge,
      });

      if (!verifyRes.success || !verifyRes.data) {
        throw new Error("Login verification failed");
      }

      login(verifyRes.data.token, verifyRes.data.user as User);
      const returnTo = searchParams.get("returnTo");
      const fallback = window.location.hostname === "console.ooo.codes" ? "/console" : "/dashboard";
      navigate(returnTo || fallback);
    } catch (err) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError(t("login.auth_cancelled"));
      } else {
        const msg = err instanceof Error ? err.message : t("login.failed");
        // 서버가 반환하는 상태 에러를 i18n 메시지로 변환
        if (msg === "pending_approval") {
          setError(t("login.pending_approval"));
        } else if (msg === "account_suspended") {
          setError(t("login.account_suspended"));
        } else if (msg === "account_banned") {
          setError(t("login.account_banned"));
        } else {
          setError(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSetup(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const optionsRes = await auth.registerOptions({
        username,
        display_name: displayName,
      });

      if (!optionsRes.success || !optionsRes.data) {
        throw new Error("Failed to get registration options");
      }

      const { options } = optionsRes.data as { options: unknown };

      const credential = await startRegistration({
        optionsJSON:
          options as Parameters<typeof startRegistration>[0]["optionsJSON"],
      });

      const verifyRes = await auth.registerVerify({
        credential,
        username,
        display_name: displayName,
        challenge: (options as { challenge: string }).challenge,
      });

      if (!verifyRes.success || !verifyRes.data) {
        throw new Error("Registration verification failed");
      }

      // pending 상태 가입 처리
      if (verifyRes.data.pending_approval) {
        setError(null);
        setMode("login");
        setSuccessMessage(t("login.signup_success"));
        return;
      }

      login(verifyRes.data.token, verifyRes.data.user as User);
      const returnTo = searchParams.get("returnTo");
      const fallback = window.location.hostname === "console.ooo.codes" ? "/console" : "/dashboard";
      navigate(returnTo || fallback);
    } catch (err) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError(t("setup.cancelled"));
      } else {
        setError(err instanceof Error ? err.message : t("setup.failed"));
      }
    } finally {
      setLoading(false);
    }
  }

  function handleInviteSubmit(e: React.FormEvent) {
    e.preventDefault();
    const raw = inviteCode.trim();
    if (!raw) return;
    const uuidMatch = raw.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i);
    const token = uuidMatch ? uuidMatch[1] : raw;
    navigate(`/invite/${token}`);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
      {/* Top-right controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {/* Language switcher */}
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value as Locale)}
          className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          {(Object.entries(LOCALE_LABELS) as [Locale, string][]).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        {/* Theme toggle */}
        <button
          onClick={toggle}
          className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
          aria-label={t("nav.toggle_theme")}
        >
          {theme === "dark" ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
            </svg>
          )}
        </button>
      </div>

      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-brand-600 dark:text-brand-400">
            {t("app.name")}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t("app.subtitle")}
          </p>
        </div>

        <div className="card p-6 space-y-4">
          {mode === "setup" ? (
            <>
              <div className="text-center space-y-1">
                <h2 className="text-lg font-semibold">{t("setup.title")}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("setup.subtitle")}
                </p>
              </div>

              {error && <div className="error-box">{error}</div>}

              <form onSubmit={handleSetup} className="space-y-4">
                <div>
                  <label htmlFor="username" className="label">
                    {t("setup.username")}
                  </label>
                  <input
                    id="username"
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin"
                    className="input-field"
                  />
                </div>

                <div>
                  <label htmlFor="displayName" className="label">
                    {t("setup.display_name")}
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your Name"
                    className="input-field"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3"
                >
                  {loading ? t("setup.setting_up") : t("setup.button")}
                </button>
              </form>

              {!needsSetup && (
                <button
                  onClick={() => setMode("login")}
                  className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
                >
                  {t("setup.already_have_account")}
                </button>
              )}
            </>
          ) : mode === "signup" ? (
            <>
              <div className="text-center space-y-1">
                <h2 className="text-lg font-semibold">{t("login.signup_title")}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("login.signup_subtitle")}
                </p>
              </div>

              {error && <div className="error-box">{error}</div>}

              <form onSubmit={handleSetup} className="space-y-4">
                <div>
                  <label htmlFor="signup-username" className="label">
                    {t("setup.username")}
                  </label>
                  <input
                    id="signup-username"
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="username"
                    className="input-field"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="signup-displayName" className="label">
                    {t("setup.display_name")}
                  </label>
                  <input
                    id="signup-displayName"
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your Name"
                    className="input-field"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3"
                >
                  {loading ? t("setup.setting_up") : t("setup.button")}
                </button>
              </form>

              <button
                onClick={() => setMode("invite")}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
              >
                {t("login.have_invite")}
              </button>

              <button
                onClick={() => setMode("login")}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
              >
                {t("setup.already_have_account")}
              </button>
            </>
          ) : mode === "invite" ? (
            <>
              <h2 className="text-lg font-semibold text-center">
                {t("login.have_invite")}
              </h2>

              <form onSubmit={handleInviteSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder={t("login.invite_placeholder")}
                    className="input-field"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={!inviteCode.trim()}
                  className="w-full btn-primary py-3"
                >
                  {t("login.join")}
                </button>
              </form>

              <button
                onClick={() => setMode("login")}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
              >
                {t("login.back_to_login")}
              </button>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-center">
                {t("login.title")}
              </h2>

              {successMessage && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-3 text-sm text-green-700 dark:text-green-300">
                  {successMessage}
                </div>
              )}

              {error && <div className="error-box">{error}</div>}

              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                </svg>
                {loading ? t("login.authenticating") : t("login.button")}
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t("login.passkey_info")}
              </p>

              <div className="relative flex items-center gap-4 py-1">
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                <span className="text-xs text-gray-400 dark:text-gray-500">{t("login.or")}</span>
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
              </div>

              <button
                onClick={() => setMode("signup")}
                className="w-full py-3 rounded-lg border-2 border-brand-600 dark:border-brand-400 text-brand-600 dark:text-brand-400 font-semibold hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
              >
                {t("login.signup_button")}
              </button>

              {needsSetup && (
                <button
                  onClick={() => setMode("setup")}
                  className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
                >
                  {t("login.first_time")}
                </button>
              )}
            </>
          )}
        </div>

        {/* Daily usage progress bar */}
        {usageData && (() => {
          const pct = Math.min((usageData.requests_today / usageData.daily_limit) * 100, 100);
          const barColor = pct >= 80 ? "bg-red-500" : pct >= 50 ? "bg-yellow-500" : "bg-brand-500";
          return (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-900/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("login.usage_today")}
                </span>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  {usageData.requests_today.toLocaleString()} / {(usageData.daily_limit / 1000).toFixed(0)}K
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor}`}
                  style={{ width: `${Math.max(pct, 0.5)}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500 text-right">
                {pct.toFixed(1)}% {t("login.used")}
              </p>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
